import File from "Entity/File";
import Product from "Entity/Product";

/**
 * A reload takes whatever the server holds now, so the one thing the user cannot work out for themselves
 * is which rows appeared while their window was open. In a share that runs to hundreds of rows, being
 * told it changed is not enough to find them by.
 */

export type ShareItem = File | Product

export interface ShareItems {
    buckets: {[slug: string]: File[]}
    catalogs: {[slug: string]: Product[]}
}

export type ShareCollection = 'buckets' | 'catalogs'

export const COLLECTIONS: ShareCollection[] = ['buckets', 'catalogs']

export const entryKey = (collection: ShareCollection, slug: string, id: number): string =>
    `${collection}/${slug}/${id}`

const listAt = (items: ShareItems | undefined, collection: ShareCollection, slug: string): ShareItem[] =>
    (items?.[collection] as {[slug: string]: ShareItem[]} | undefined)?.[slug] ?? []

const idsOf = (list: ShareItem[]): Set<number> => {
    const ids = new Set<number>()

    list.forEach(item => {
        if ('number' === typeof item.id) {
            ids.add(item.id)
        }
    })

    return ids
}

/**
 * @param baseline the copy the window was built on
 * @param fresh    the copy the server holds now
 */
export const arrivedSince = (baseline: ShareItems, fresh: ShareItems): Set<string> => {
    const arrived = new Set<string>()

    COLLECTIONS.forEach(collection => {
        Object.keys(fresh[collection] ?? {}).forEach(slug => {
            const held = idsOf(listAt(baseline, collection, slug))

            idsOf(listAt(fresh, collection, slug)).forEach(id => {
                if (!held.has(id)) {
                    arrived.add(entryKey(collection, slug, id))
                }
            })
        })
    })

    return arrived
}

/** A copy of what is held, which is the baseline the next reload is compared against. */
export const snapshotItems = (items: ShareItems): ShareItems => ({
    buckets: Object.fromEntries(Object.entries(items.buckets).map(([slug, list]) => [slug, [...list]])),
    catalogs: Object.fromEntries(Object.entries(items.catalogs).map(([slug, list]) => [slug, [...list]])),
})
